import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Search, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import landscapeBg from '@/assets/bg_landscape.png';

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Redirect logged-in users to their dashboards
  useEffect(() => {
    if (user) {
      if (user.role === 'provider') {
        navigate('/provider/dashboard', { replace: true });
      } else if (user.role === 'seeker') {
        navigate('/seeker/dashboard', { replace: true });
      } else if (user.role === 'admin') {
        navigate('/admin/dashboard', { replace: true });
      }
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div
        className="relative bg-gradient-to-b from-secondary to-white py-20 md:py-32"
        style={{
          backgroundImage: `url(${landscapeBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-white/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#2C3333] mb-6 tracking-tight">
            Find Your Mental Health Support
          </h1>
          <p className="text-lg md:text-xl text-[#4B5563] mb-8 max-w-2xl mx-auto leading-relaxed">
            Connect with qualified therapists, psychologists, and counsellors in your area. Begin your journey to wellness today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="get-started-btn"
              onClick={() => navigate('/auth')}
              className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5"
            >
              Get Started
            </Button>
            <Button
              data-testid="browse-providers-btn"
              onClick={() => navigate('/auth')}
              className="bg-secondary hover:bg-[#EDE8DE] text-primary rounded-full px-8 py-6 text-lg font-medium transition-all"
            >
              Browse Providers
            </Button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-center text-[#2C3333] mb-12">
            Why Choose HappyTring
          </h2>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-[#F9FAFB] rounded-2xl p-8 hover:bg-white hover:shadow-sm transition-all" data-testid="feature-verified">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-3 text-[#2C3333]">Verified Professionals</h3>
              <p className="text-[#4B5563] leading-relaxed">All our providers are qualified mental health professionals ready to support you.</p>
            </div>
            <div className="bg-[#F9FAFB] rounded-2xl p-8 hover:bg-white hover:shadow-sm transition-all" data-testid="feature-easy">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Search className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-3 text-[#2C3333]">Easy to Find</h3>
              <p className="text-[#4B5563] leading-relaxed">Filter by location, specialization, and session type to find the perfect match for your needs.</p>
            </div>
            <div className="bg-[#F9FAFB] rounded-2xl p-8 hover:bg-white hover:shadow-sm transition-all" data-testid="feature-simple">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-3 text-[#2C3333]">Simple Connection</h3>
              <p className="text-[#4B5563] leading-relaxed">Express interest with one click. Providers will reach out to you directly.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing;